import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Building2, GraduationCap, Rocket, TrendingUp, MapPin, Lock, Sparkles, DollarSign, Compass, Sun, CalendarDays, Download } from "lucide-react";
import { Link } from "react-router-dom";

const LEVELS = [
  { key: "global", label_fr: "Mondial", label_en: "Global" },
  { key: "country", label_fr: "Pays", label_en: "Country" },
  { key: "province", label_fr: "Province / Région", label_en: "Province / Region" },
  { key: "city", label_fr: "Ville", label_en: "City" },
];

const STAT_ACCENT_COLORS = {
  navy: "#1E3A8A",
  orange: "#F97316",
  gold: "#F59E0B",
  green: "#16A34A",
};

function Stat({ icon: Icon, label, value, hint, accent = "navy" }) {
  const c = STAT_ACCENT_COLORS[accent] || STAT_ACCENT_COLORS.navy;
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5">
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-[0.2em]">
        <Icon className="w-3.5 h-3.5" style={{ color: c }} /> {label}
      </div>
      <p className="font-display text-3xl font-bold mt-2" style={{ color: c }}>{value ?? "—"}</p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function LockedRow({ children }) {
  return (
    <div className="relative">
      <div className="opacity-40 pointer-events-none blur-[2px]">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center">
        <Lock className="w-4 h-4 text-slate-400" />
      </div>
    </div>
  );
}

const COMPARE_GRADIENTS = [
  "bg-gradient-to-br from-[#1E3A8A] to-[#172554]",
  "bg-gradient-to-br from-[#F97316] to-[#EA580C]",
  "bg-gradient-to-br from-[#16A34A] to-[#065F46]",
];
const COMPARE_LABELS = ["A", "B", "C"];

export default function Analytics() {
  const { user, lang } = useAuth();
  const [locations, setLocations] = useState({ countries: [], cities_by_country: {} });
  const [level, setLevel] = useState("country");
  const [country, setCountry] = useState("France");
  const [city, setCity] = useState("");
  const [sector, setSector] = useState("");
  const [career, setCareer] = useState("");
  const [rh, setRH] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [compare, setCompare] = useState(null);
  const [compareB, setCompareB] = useState({ country: "United Kingdom", city: "" });
  const [compareC, setCompareC] = useState({ country: "", city: "" });
  const [tab, setTab] = useState("main");

  const isEmployer = user?.user_type === "company" || user?.user_type === "admin";

  useEffect(() => { http.get("/analytics/locations").then((r) => setLocations(r.data)); }, []);

  const load = async () => {
    const params = {};
    if (country) params.country = country;
    if (level === "city" && city) params.city = city;
    if (sector) params.sector = sector;
    if (career) params.career = career;
    if (isEmployer) {
      const { data } = await http.get("/stats/rh-intelligence", { params });
      setRH(data);
    } else {
      const { data } = await http.get("/stats/careers", { params });
      setCandidate(data);
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-line */ }, [user, level, country, city, sector, career]);

  const runCompare = async () => {
    const params = { country_a: country };
    if (city) params.city_a = city;
    if (compareB.country) params.country_b = compareB.country;
    if (compareB.city) params.city_b = compareB.city;
    if (compareC.country) params.country_c = compareC.country;
    if (compareC.city) params.city_c = compareC.city;
    const { data } = await http.get("/analytics/compare", { params });
    setCompare(data);
  };

  const downloadComparePdf = async () => {
    const params = new URLSearchParams();
    params.set("country_a", country);
    if (city) params.set("city_a", city);
    if (compareB.country) params.set("country_b", compareB.country);
    if (compareB.city) params.set("city_b", compareB.city);
    if (compareC.country) params.set("country_c", compareC.country);
    if (compareC.city) params.set("city_c", compareC.city);
    const resp = await http.get(`/analytics/compare/export.pdf?${params.toString()}`, { responseType: "blob" });
    const blob = new Blob([resp.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const parts = [country];
    if (city) parts.push(city);
    if (compareB.country) parts.push(compareB.country);
    if (compareB.city) parts.push(compareB.city);
    if (compareC.country) parts.push(compareC.country);
    if (compareC.city) parts.push(compareC.city);
    a.download = `proxijob_analytics_${parts.join("_").replace(/\s+/g, "-")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const availableCities = locations.cities_by_country[country] || [];
  const availableCitiesB = locations.cities_by_country[compareB.country] || [];
  const availableCitiesC = locations.cities_by_country[compareC.country] || [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] via-[#1E3A8A] to-[#0F172A] text-white p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#F97316]/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-[#F97316]" />
              <span className="text-xs uppercase tracking-[0.3em] text-white/70">
                Data Intelligence · {isEmployer ? "RH View" : (lang === "fr" ? "Candidat View" : "Candidate View")}
              </span>
            </div>
            <h1 data-testid="analytics-title" className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
              {isEmployer
                ? (lang === "fr" ? "RH Intelligence — Marché local" : "HR Intelligence — Local market")
                : (lang === "fr" ? "GPS Carrière & Marché du travail" : "Career GPS & labour market")}
            </h1>
            <p className="mt-3 text-white/80 max-w-3xl leading-relaxed">
              {isEmployer
                ? (lang === "fr" ? "Anticipez la disponibilité future des talents, ajustez votre stratégie de recrutement." : "Anticipate future talent availability, sharpen your hiring strategy.")
                : (lang === "fr" ? "Évaluez vos chances d'embauche, benchmark salaire local, taux d'insertion pro." : "Evaluate hiring odds, local salary benchmark, professional insertion rate.")}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-2 flex-wrap">
          <button data-testid="tab-main" onClick={() => setTab("main")} className={`px-4 py-2 rounded-full text-sm font-medium ${tab === "main" ? "bg-[#1E3A8A] text-white" : "bg-white border border-slate-200"}`}>{isEmployer ? "RH Dashboard" : "GPS Carrière"}</button>
          <button data-testid="tab-compare" onClick={() => setTab("compare")} className={`px-4 py-2 rounded-full text-sm font-medium ${tab === "compare" ? "bg-[#1E3A8A] text-white" : "bg-white border border-slate-200"}`}>{lang === "fr" ? "Comparer 3 villes" : "Compare 3 cities"}</button>
        </div>

        {/* Filters */}
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 grid md:grid-cols-4 gap-4">
          <div>
            <Label>{lang === "fr" ? "Granularité" : "Granularity"}</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger data-testid="analytics-level" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{LEVELS.map((l) => <SelectItem key={l.key} value={l.key}>{l[`label_${lang}`]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t(lang, "analytics_country_label")}</Label>
            <Select value={country || "__none__"} onValueChange={(v) => { setCountry(v === "__none__" ? "" : v); setCity(""); }}>
              <SelectTrigger data-testid="analytics-country" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">—</SelectItem>{locations.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{lang === "fr" ? "Ville" : "City"}</Label>
            <Select value={city || "__none__"} onValueChange={(v) => setCity(v === "__none__" ? "" : v)} disabled={level !== "city"}>
              <SelectTrigger data-testid="analytics-city" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">—</SelectItem>{availableCities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{lang === "fr" ? "Métier / Secteur" : "Career / Sector"}</Label>
            <Input data-testid="analytics-career" value={career} onChange={(e) => setCareer(e.target.value)} placeholder={isEmployer ? "Tech / SaaS..." : "informatique, cuisine..."} className="mt-1.5" />
          </div>
        </div>

        {/* MAIN TAB */}
        {tab === "main" && (
          <>
            {/* CANDIDATE VIEW */}
            {!isEmployer && candidate && (
              <div className="mt-8 space-y-8 fade-up">
                <div className="grid md:grid-cols-4 gap-4">
                  <Stat icon={Compass} label={lang === "fr" ? "Indice de tension" : "Tension index"} value={candidate.tension.verdict} accent={candidate.tension.color === "green" ? "green" : candidate.tension.color === "red" ? "orange" : "gold"} />
                  <Stat icon={Building2} label={lang === "fr" ? "Entreprises trouvées" : "Companies found"} value={candidate.companies_snapshot.total} hint={`${candidate.companies_snapshot.student_friendly} ${lang === "fr" ? "acceptent des étudiants" : "student-friendly"}`} />
                  <Stat icon={Rocket} label={lang === "fr" ? "Postes ouverts" : "Open positions"} value={candidate.tension.open_positions} />
                  <Stat icon={TrendingUp} label={lang === "fr" ? "Taux d'insertion" : "Insertion rate"} value={`${candidate.career_gps.insertion_rate_estimate}%`} accent="green" />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="rounded-2xl bg-white border border-slate-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <DollarSign className="w-4 h-4 text-[#16A34A]" />
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Benchmark salarial" : "Salary benchmark"} · {candidate.salary.sector_ref}</p>
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-xl bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Junior (0-2 {lang === "fr" ? "ans" : "y"})</p>
                        <p className="font-display text-2xl font-bold text-[#1E3A8A]">
                          {candidate.salary.junior.min.toLocaleString()} – {candidate.salary.junior.max.toLocaleString()} {candidate.salary.currency}
                        </p>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Senior (3+ {lang === "fr" ? "ans" : "y"})</p>
                        <p className="font-display text-2xl font-bold text-[#1E3A8A]">
                          {candidate.salary.senior.min.toLocaleString()} – {candidate.salary.senior.max.toLocaleString()} {candidate.salary.currency}
                        </p>
                      </div>
                      <div className="rounded-xl bg-[#FFF7ED] border border-[#F97316]/30 p-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-[#C2410C]">{lang === "fr" ? "Taux horaire étudiant / été" : "Student / summer hourly"}</p>
                        <p className="font-display text-2xl font-bold text-[#C2410C]">{candidate.salary.student_hourly} {candidate.salary.currency}/h</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white border border-slate-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="w-4 h-4 text-[#F97316]" />
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Avantages fréquents" : "Common benefits"}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {candidate.benefits_common.map((b) => <Badge key={b} variant="secondary" className="rounded-full">{b}</Badge>)}
                    </div>
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">{lang === "fr" ? "Parcours suivis par les pros" : "Diploma paths"}</p>
                      {candidate.career_gps.diploma_paths.map((d) => (
                        <div key={d.path} className="flex items-center justify-between py-1">
                          <span className="text-sm">{d.path}</span>
                          <Badge variant="secondary" className="rounded-full">{d.share}%</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl bg-white border border-slate-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-4 h-4 text-[#1E3A8A]" />
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Où recruter votre profil" : "Where you can be hired"}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {candidate.companies_snapshot.top_cities.map((c) => (
                      <div key={c.place} className="rounded-full border border-slate-200 px-3 py-1.5 text-sm flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-slate-400" /> {c.place} <Badge variant="secondary" className="rounded-full text-[10px]">{c.count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* EMPLOYER RH VIEW */}
            {isEmployer && rh && (
              <div className="mt-8 space-y-8 fade-up">
                {!rh.unlocked && (
                  <div className="rounded-2xl bg-gradient-to-r from-[#F97316]/20 to-[#1E3A8A]/20 border border-[#F97316]/40 p-5 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-[#C2410C]" />
                      <p className="text-sm text-slate-800">{rh.upgrade_message}</p>
                    </div>
                    <Link to="/pricing">
                      <Button data-testid="upsell-premium" className="bg-[#F97316] hover:bg-[#EA580C] rounded-full">Débloquer Premium</Button>
                    </Link>
                  </div>
                )}

                <div className="grid md:grid-cols-4 gap-4">
                  <Stat icon={Users} label="Juniors (0-2y)" value={<span className="flex items-center gap-1">{rh.pressure.juniors}{!rh.unlocked && <Lock className="w-4 h-4 opacity-50" />}</span>} accent="navy" />
                  <Stat icon={Users} label="Seniors (3+y)" value={<span className="flex items-center gap-1">{rh.pressure.seniors}{!rh.unlocked && <Lock className="w-4 h-4 opacity-50" />}</span>} accent="gold" />
                  <Stat icon={Rocket} label={lang === "fr" ? "Postes ouverts" : "Open positions"} value={rh.pressure.open_positions} accent="orange" />
                  <Stat icon={TrendingUp} label={lang === "fr" ? "Candidats/poste" : "Candidates/position"} value={<span className="flex items-center gap-1">{rh.pressure.candidates_per_position ?? "—"}{!rh.unlocked && <Lock className="w-4 h-4 opacity-50" />}</span>} />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="rounded-2xl bg-white border border-slate-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <GraduationCap className="w-4 h-4 text-[#1E3A8A]" />
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Offre future — Diplômés attendus" : "Future supply — Expected graduates"}</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between"><span className="text-sm">{lang === "fr" ? "Intentions carrière (roadmaps)" : "Career intents"}</span><Badge className="bg-[#1E3A8A] text-white rounded-full">{rh.future_supply.career_intents}</Badge></div>
                      <div className="flex justify-between"><span className="text-sm">{lang === "fr" ? "Diplômés dans 6 mois" : "Graduates in 6 months"}</span><Badge variant="secondary" className="rounded-full">{rh.future_supply.expected_graduates_6m}</Badge></div>
                      <div className="flex justify-between"><span className="text-sm">{lang === "fr" ? "Diplômés dans 1 an" : "Graduates in 1 year"}</span><Badge variant="secondary" className="rounded-full">{rh.future_supply.expected_graduates_1y}</Badge></div>
                      <div className="flex justify-between"><span className="text-sm">{lang === "fr" ? "Diplômés dans 2 ans" : "Graduates in 2 years"}</span><Badge variant="secondary" className="rounded-full">{rh.future_supply.expected_graduates_2y}</Badge></div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{lang === "fr" ? "Diplômes en formation" : "Diploma distribution"}</p>
                      {rh.unlocked ? (rh.future_supply.diploma_distribution || []).map((d) => (
                        <div key={d.diploma} className="flex justify-between py-1"><span className="text-sm truncate">{d.diploma || "—"}</span><Badge variant="secondary" className="rounded-full">{d.count}</Badge></div>
                      )) : (
                        <LockedRow><p className="text-sm">🔒 Distribution disponible en Premium</p></LockedRow>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white border border-slate-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Sun className="w-4 h-4 text-[#F97316]" />
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Vivier saisonnier & temporel" : "Seasonal talent pool"}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-xl bg-[#FFF7ED] border border-[#F97316]/30 p-3 text-center">
                        <Sun className="w-4 h-4 text-[#C2410C] mx-auto" />
                        <p className="text-[10px] uppercase text-[#C2410C] mt-1 font-semibold">{lang === "fr" ? "Été" : "Summer"}</p>
                        <p className="font-display text-2xl font-bold text-[#C2410C]">{rh.seasonal_pool.summer}</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center">
                        <CalendarDays className="w-4 h-4 text-[#1E3A8A] mx-auto" />
                        <p className="text-[10px] uppercase text-[#1E3A8A] mt-1 font-semibold">Week-end</p>
                        <p className="font-display text-2xl font-bold text-[#1E3A8A]">{rh.seasonal_pool.weekend}</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center">
                        <CalendarDays className="w-4 h-4 text-slate-500 mx-auto" />
                        <p className="text-[10px] uppercase text-slate-500 mt-1 font-semibold">{lang === "fr" ? "Fêtes" : "Holidays"}</p>
                        <p className="font-display text-2xl font-bold text-slate-700">{rh.seasonal_pool.holidays}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                      {lang === "fr" ? "Nombre d'étudiants inscrits dans les classes Proxijob disponibles pour ces périodes." : "Students registered in Proxijob classes available for these periods."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* COMPARE TAB */}
        {tab === "compare" && (
          <div className="mt-8 space-y-6">
            <div className="rounded-2xl bg-white border border-slate-200 p-5 grid md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
              <div>
                <Label>{lang === "fr" ? "Zone B — Pays" : "Zone B — Country"}</Label>
                <Select value={compareB.country} onValueChange={(v) => setCompareB({ country: v, city: "" })}>
                  <SelectTrigger data-testid="compare-country-b" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{locations.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{lang === "fr" ? "Zone B — Ville" : "Zone B — City"}</Label>
                <Select value={compareB.city || "__none__"} onValueChange={(v) => setCompareB({ ...compareB, city: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="compare-city-b" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">—</SelectItem>{availableCitiesB.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{lang === "fr" ? "Zone C — Pays" : "Zone C — Country"}</Label>
                <Select value={compareC.country || "__none__"} onValueChange={(v) => setCompareC({ country: v === "__none__" ? "" : v, city: "" })}>
                  <SelectTrigger data-testid="compare-country-c" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">—</SelectItem>{locations.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{lang === "fr" ? "Zone C — Ville" : "Zone C — City"}</Label>
                <Select value={compareC.city || "__none__"} onValueChange={(v) => setCompareC({ ...compareC, city: v === "__none__" ? "" : v })} disabled={!compareC.country}>
                  <SelectTrigger data-testid="compare-city-c" className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">—</SelectItem>{availableCitiesC.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button data-testid="compare-run" onClick={runCompare} className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full h-11">
                <Compass className="w-4 h-4 mr-1.5" /> {lang === "fr" ? "Comparer" : "Compare"}
              </Button>
            </div>

            {compare?.a && (
              <div className="flex justify-end -mt-2">
                <Button
                  data-testid="compare-download-pdf"
                  onClick={downloadComparePdf}
                  variant="outline"
                  className="rounded-full h-9 text-xs border-[#F97316] text-[#F97316] hover:bg-[#FFF7ED] hover:text-[#EA580C]"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" /> {lang === "fr" ? "Télécharger en PDF" : "Download as PDF"}
                </Button>
              </div>
            )}

            {compare?.a && (
              <div className={`grid gap-6 fade-up ${compare?.c ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                {[compare.a, compare.b, compare.c].filter(Boolean).map((z, i) => {
                  return (
                    <div key={`compare-${z.label || COMPARE_LABELS[i]}`} data-testid={`compare-zone-${i}`} className={`rounded-3xl p-6 text-white ${COMPARE_GRADIENTS[i]}`}>
                      <div className="flex items-center gap-2 mb-4">
                        <MapPin className="w-4 h-4" />
                        <p className="text-xs uppercase tracking-[0.2em] opacity-80">Zone {COMPARE_LABELS[i]}</p>
                      </div>
                      <h3 className="font-display text-3xl font-bold">{z.label}</h3>
                      <div className="mt-6 grid grid-cols-2 gap-3">
                        <div><p className="text-xs opacity-70">Entreprises</p><p className="font-display text-3xl font-bold">{z.companies}</p></div>
                        <div><p className="text-xs opacity-70">Postes ouverts</p><p className="font-display text-3xl font-bold">{z.open_positions}</p></div>
                        <div><p className="text-xs opacity-70">Candidatures</p><p className="font-display text-3xl font-bold">{z.applications}</p></div>
                        <div><p className="text-xs opacity-70">Étudiants OK</p><p className="font-display text-3xl font-bold">{z.student_friendly}</p></div>
                      </div>
                      <div className="mt-6 pt-4 border-t border-white/20">
                        <p className="text-xs opacity-70 mb-2">Top secteurs</p>
                        <div className="space-y-1">
                          {z.top_sectors.slice(0, 4).map((s) => (
                            <div key={s.sector} className="flex justify-between text-sm"><span>{s.sector}</span><span className="opacity-80">{s.count}</span></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
